import type { Task, TaskResult } from "../../../open-api";
import type { TaskDef } from "../../../open-api/generated";
import { registerWorker, type RegisteredWorker } from "./registry";
import { simpleTask } from "../../builders/tasks/simple";
import { generateSchemaFromClass } from "../schema/decorators";

/**
 * Options for the @worker decorator.
 */
export interface WorkerOptions {
  /** 
   * Task definition name (must match workflow task name).
   * This is the only required parameter.
   */
  taskDefName: string;

  /**
   * Maximum concurrent tasks this worker can execute.
   * - Default: 1
   * - Controls concurrency level for task execution
   * - Choose based on workload:
   *   * CPU-bound: 1-4
   *   * I/O-bound: 10-50
   *   * Mixed: 5-20
   */
  concurrency?: number;

  /**
   * Polling interval in milliseconds.
   * - Default: 100ms
   * - Lower values = more responsive but higher server load
   * - Higher values = less server load but slower task pickup
   * - Recommended: 100-500ms for most use cases
   */
  pollInterval?: number;

  /**
   * Task domain for multi-tenancy.
   * - Default: undefined (no domain isolation)
   * - Use when you need to partition tasks across different environments/tenants
   */
  domain?: string;

  /**
   * Unique worker identifier.
   * - Default: undefined (auto-generated)
   * - Useful for debugging and tracking which worker executed which task
   */
  workerId?: string;

  /**
   * Auto-register task definition on startup.
   * - Default: false
   * - When true: Task definition is created/updated on worker startup
   * - When false: Task definition must exist in Conductor already
   * - Recommended: false for production (manage task definitions separately)
   */
  registerTaskDef?: boolean;

  /**
   * Server-side long poll timeout in milliseconds.
   * - Default: 100ms
   * - How long the server will wait for a task before returning empty response
   * - Higher values reduce polling frequency when no tasks available
   * - Recommended: 100-500ms
   */
  pollTimeout?: number;

  /**
   * Task definition template for registration.
   * - Default: undefined
   * - Only used when registerTaskDef=true
   * - Allows specifying retry policies, timeouts, rate limits, etc.
   * - The taskDefName parameter takes precedence for the name field
   */
  taskDef?: TaskDef;

  /**
   * Overwrite existing task definitions on server.
   * - Default: true
   * - When true: Always updates task definition
   * - When false: Only creates if doesn't exist
   * - Can be overridden via env: CONDUCTOR_WORKER_<NAME>_OVERWRITE_TASK_DEF=false
   */
  overwriteTaskDef?: boolean;

  /**
   * Enforce strict JSON schema validation.
   * - Default: false
   * - When false: additionalProperties=true (allows extra fields)
   * - When true: additionalProperties=false (strict validation)
   * - Can be overridden via env: CONDUCTOR_WORKER_<NAME>_STRICT_SCHEMA=true
   */
  strictSchema?: boolean;

  /**
   * JSON Schema for task input validation.
   * - Registered alongside the task definition when registerTaskDef=true
   * - Use the `jsonSchema()` helper to generate from field descriptors
   */
  inputSchema?: Record<string, unknown>;

  /**
   * JSON Schema for task output validation.
   * - Registered alongside the task definition when registerTaskDef=true
   * - Use the `jsonSchema()` helper to generate from field descriptors
   */
  outputSchema?: Record<string, unknown>;

  /**
   * Class decorated with `@schemaField()` for auto-generating input schema.
   * - Alternative to `inputSchema` — schema is generated from class metadata
   * - Takes precedence over `inputSchema` if both are set
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputType?: new (...args: any[]) => unknown;

  /**
   * Class decorated with `@schemaField()` for auto-generating output schema.
   * - Alternative to `outputSchema` — schema is generated from class metadata
   * - Takes precedence over `outputSchema` if both are set
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  outputType?: new (...args: any[]) => unknown;

  /**
   * Enable automatic lease extension (heartbeat) for long-running tasks.
   * - Default: false
   * - When true: sends periodic heartbeats at 80% of the task's responseTimeoutSeconds
   * - Only applies to tasks where responseTimeoutSeconds >= 1.25s (interval = timeout * 0.8 * 1000 >= 1000ms)
   * - Can be overridden via env: CONDUCTOR_WORKER_<NAME>_LEASE_EXTEND_ENABLED=true
   */
  leaseExtendEnabled?: boolean;
}

/**
 * Decorator to register a function as a Conductor worker.
 * 
 * This decorator enables SDK-style worker registration with auto-discovery,
 * matching the Python SDK's @worker_task pattern.
 * 
 * @param options - Worker configuration options
 * 
 * @example
 * Basic usage:
 * ```typescript
 * @worker({ taskDefName: "process_order" })
 * async function processOrder(task: Task): Promise<TaskResult> {
 *   const orderId = task.inputData.orderId;
 *   // Process order logic
 *   return {
 *     status: "COMPLETED",
 *     outputData: { orderId, processed: true },
 *   };
 * }
 * ```
 * 
 * @example
 * With concurrency:
 * ```typescript
 * @worker({ taskDefName: "send_email", concurrency: 10 })
 * async function sendEmail(task: Task): Promise<TaskResult> {
 *   const { to, subject, body } = task.inputData;
 *   await emailService.send(to, subject, body);
 *   return { status: "COMPLETED", outputData: { sent: true } };
 * }
 * ```
 * 
 * @example
 * With domain and custom polling:
 * ```typescript
 * @worker({
 *   taskDefName: "validate_payment",
 *   domain: "payments",
 *   concurrency: 5,
 *   pollInterval: 200,
 * })
 * async function validatePayment(task: Task): Promise<TaskResult> {
 *   // Validation logic
 *   return { status: "COMPLETED", outputData: { valid: true } };
 * }
 * ```
 * 
 * @example
 * With task definition registration:
 * ```typescript
 * @worker({
 *   taskDefName: "complex_task",
 *   registerTaskDef: true,
 *   taskDef: {
 *     retryCount: 3,
 *     retryLogic: "EXPONENTIAL_BACKOFF",
 *     timeoutSeconds: 300,
 *   },
 * })
 * async function complexTask(task: Task): Promise<TaskResult> {
 *   // Complex logic
 *   return { status: "COMPLETED", outputData: { result: "..." } };
 * }
 * ```
 * 
 * @example
 * Non-retryable errors:
 * ```typescript
 * import { worker, NonRetryableException } from "@io-orkes/conductor-javascript/worker";
 * 
 * @worker({ taskDefName: "validate_order" })
 * async function validateOrder(task: Task): Promise<TaskResult> {
 *   const order = await getOrder(task.inputData.orderId);
 *   
 *   if (!order) {
 *     // Order doesn't exist - retry won't help
 *     throw new NonRetryableException(`Order ${task.inputData.orderId} not found`);
 *   }
 *   
 *   return { status: "COMPLETED", outputData: { validated: true } };
 * }
 * ```
 */
/** Minimal context shape for Stage 3 method decorators (TypeScript 5.0+). */
interface MethodDecoratorContext {
  kind: string;
  name: string | symbol;
}

/**
 * Type guard for Stage 3 (TypeScript 5.0+) decorator context.
 * New decorators pass (value, context) where context has a `kind` property.
 */
function isNewDecoratorContext(arg: unknown): arg is MethodDecoratorContext {
  return (
    typeof arg === "object" &&
    arg !== null &&
    "kind" in arg &&
    typeof (arg as { kind: string }).kind === "string"
  );
}

type WorkerMethod = (
  task: Task
) => Promise<Omit<TaskResult, "workflowInstanceId" | "taskId">>;

export function worker(options: WorkerOptions) {
  function decorator<T extends WorkerMethod>(
    value: T,
    context: MethodDecoratorContext
  ): T | undefined;
  function decorator(
    target: object,
    propertyKey?: string,
    descriptor?: PropertyDescriptor
  ): PropertyDescriptor | WorkerMethod | undefined;
  function decorator<T extends WorkerMethod>(
    target: T | object,
    propertyKeyOrContext?: string | MethodDecoratorContext,
    descriptor?: PropertyDescriptor
  ): T | PropertyDescriptor | undefined {
    // Detect decorator API: new (Stage 3) vs legacy (experimentalDecorators)
    let executeFunction: (task: Task) => Promise<Omit<TaskResult, "workflowInstanceId" | "taskId">>;
    let isNewApi = false;

    if (isNewDecoratorContext(propertyKeyOrContext)) {
      // New decorator API: target is the method itself
      executeFunction = target as (task: Task) => Promise<
        Omit<TaskResult, "workflowInstanceId" | "taskId">
      >;
      isNewApi = true;
    } else {
      // Legacy API: descriptor?.value (method) or target (standalone function)
      const fn = (descriptor?.value ?? target) as (
        task: Task
      ) => Promise<Omit<TaskResult, "workflowInstanceId" | "taskId">>;
      executeFunction = fn;
    }

    // Validate that we have a function
    if (typeof executeFunction !== "function") {
      throw new Error(
        `@worker decorator can only be applied to functions. ` +
          `Received: ${typeof executeFunction}`
      );
    }

    // Validate required options
    if (!options.taskDefName) {
      throw new Error(
        `@worker decorator requires 'taskDefName' option. ` +
          `Example: @worker({ taskDefName: "my_task" })`
      );
    }

    // Auto-generate schemas from inputType/outputType if provided
    let resolvedInputSchema = options.inputSchema;
    let resolvedOutputSchema = options.outputSchema;

    if (options.inputType) {
      resolvedInputSchema = generateSchemaFromClass(
        options.inputType
      ) as unknown as Record<string, unknown>;
    }
    if (options.outputType) {
      resolvedOutputSchema = generateSchemaFromClass(
        options.outputType
      ) as unknown as Record<string, unknown>;
    }

    // Create registered worker metadata
    const registeredWorker: RegisteredWorker = {
      taskDefName: options.taskDefName,
      executeFunction,
      concurrency: options.concurrency,
      pollInterval: options.pollInterval,
      domain: options.domain,
      workerId: options.workerId,
      registerTaskDef: options.registerTaskDef,
      pollTimeout: options.pollTimeout,
      taskDef: options.taskDef,
      overwriteTaskDef: options.overwriteTaskDef,
      strictSchema: options.strictSchema,
      inputSchema: resolvedInputSchema,
      outputSchema: resolvedOutputSchema,
      leaseExtendEnabled: options.leaseExtendEnabled,
    };

    // Register in global registry for auto-discovery
    registerWorker(registeredWorker);

    // Create dual-mode wrapper that supports both execution and workflow builder mode
    const dualModeFunction = function (this: unknown, ...args: unknown[]) {
      // Check if called in workflow builder mode:
      //   myWorker({ taskRefName: "step_1", inputParameters: { ... } })
      if (
        args.length === 1 &&
        args[0] &&
        typeof args[0] === "object" &&
        "taskRefName" in (args[0] as Record<string, unknown>)
      ) {
        const builderArgs = args[0] as {
          taskRefName: string;
          inputParameters?: Record<string, unknown>;
        };
        return simpleTask(
          builderArgs.taskRefName,
          options.taskDefName,
          builderArgs.inputParameters ?? {}
        );
      }
      // Normal execution mode
      return (executeFunction as (...args: unknown[]) => unknown).apply(
        this,
        args
      );
    };

    // Preserve original function name
    Object.defineProperty(dualModeFunction, "name", {
      value: (executeFunction as (...args: unknown[]) => unknown).name,
      configurable: true,
    });

    if (isNewApi) {
      // New decorator API: return replacement function
      return dualModeFunction as unknown as T;
    }

    // Legacy API
    if (descriptor) {
      descriptor.value = dualModeFunction;
      return descriptor;
    }
    return dualModeFunction as unknown as T;
  }

  return decorator;
}
